import { useEffect, useRef } from "react"
import { AUTH_STATUS, useAuthStore } from "@/auth"
import { homeSessionsV2QueryKey } from "@/common/queries/use-home-sessions-v2"
import { bottomNavigationQueryKey } from "@/pages/home/bottom-navigation"
import { useQueryClient } from "@tanstack/react-query"

import { v1GetBottomBar, v2GetHomeSession } from "@be-miniapp/api"
import { trackJourney } from "@be-miniapp/logging"
import { CACHE_KEYS, StorageCache } from "@be-miniapp/platform"
import {
  hasMeaningfulBottomNavigation,
  hasMeaningfulHomeSession,
} from "@be-miniapp/shared/utils/cache-guards"
import { resolveWarmupLocation } from "@be-miniapp/shared/utils/location-policy"

import { useUserPersistingData } from "@/store/user-persisting-data"
import { useUserStore } from "@/store/user-session"

const ONE_WEEK = 7 * 24 * 60 * 60 * 1000

export function useInitializeAppData() {
  const queryClient = useQueryClient()
  const isLoggedIn = useAuthStore((s) => s.status === AUTH_STATUS.AUTHENTICATED)
  const userLocation = useUserStore((state) => state.location)
  const persistedLocation = useUserPersistingData((state) => state.location)
  const accessToken = useUserStore((state) => state.access_token)

  const lastLocationKeyRef = useRef<string | null>(null)

  const warmupLocation = resolveWarmupLocation({
    userLocation,
    persistedLocation,
  })

  useEffect(() => {
    if (!isLoggedIn || !accessToken) {
      return
    }

    const locationKey = `${warmupLocation.latitude}:${warmupLocation.longitude}`

    if (lastLocationKeyRef.current === locationKey) {
      return
    }

    lastLocationKeyRef.current = locationKey

    const warmup = async () => {
      trackJourney("home_load", "start")

      const hydrateHomeSessions = queryClient.fetchQuery({
        queryKey: [
          ...homeSessionsV2QueryKey.userLocation(warmupLocation, accessToken),
        ],
        queryFn: async () => {
          const response = await v2GetHomeSession({})
          const data = response.data

          if (hasMeaningfulHomeSession(data)) {
            await StorageCache.set(CACHE_KEYS.HOME_SESSIONS, data, {
              ttl: ONE_WEEK,
            })
          } else {
            await StorageCache.remove(CACHE_KEYS.HOME_SESSIONS)
          }

          return data
        },
      })

      const hydrateBottomNav = queryClient.fetchQuery({
        queryKey: [...bottomNavigationQueryKey],
        queryFn: async () => {
          const response = await v1GetBottomBar({})
          const data = response.data

          if (hasMeaningfulBottomNavigation(data)) {
            await StorageCache.set(CACHE_KEYS.BOTTOM_NAV, data, {
              ttl: ONE_WEEK,
            })
            return data
          }

          await StorageCache.remove(CACHE_KEYS.BOTTOM_NAV)
          throw new Error("Bottom bar response has no icons")
        },
        retry: 3,
      })

      const results = await Promise.allSettled([
        hydrateHomeSessions,
        hydrateBottomNav,
      ])

      const hasFailure = results.some((result) => result.status === "rejected")
      if (hasFailure) {
        trackJourney("home_load", "fail")
        throw new Error("Home warmup failed")
      }

      trackJourney("home_load", "success")
    }

    warmup().catch(() => {
      lastLocationKeyRef.current = null
    })
  }, [accessToken, isLoggedIn, queryClient, warmupLocation])
}
